import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Upload, FileText, Table, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Importacoes = () => {
  const navigate = useNavigate();
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [uploadingPDF, setUploadingPDF] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [pdfResults, setPdfResults] = useState([]);
  
  // Estados para drag and drop
  const [isDraggingCSV, setIsDraggingCSV] = useState(false);
  const [isDraggingPDF, setIsDraggingPDF] = useState(false);
  
  const csvInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  // ── Processamento de CSV ─────────────────────────────────────────────────────
  const processCSVFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV válido');
      return;
    }

    setUploadingCSV(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.post(`${API}/import/csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setCsvResults(response.data);
      toast.success(`${response.data.imported} lembretes importados com sucesso!`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Importação CSV é exclusiva do Plano PRO!');
      } else {
        toast.error('Erro ao processar CSV: ' + (error.response?.data?.detail || 'Erro desconhecido'));
      }
    } finally {
      setUploadingCSV(false);
    }
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    processCSVFile(file);
  };

  // ── Processamento de PDF ─────────────────────────────────────────────────────
  const processPDFFile = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.pdf')) {
      toast.error('Por favor, selecione um arquivo PDF válido');
      return;
    }

    setUploadingPDF(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('lembrou_token');
      const response = await axios.post(`${API}/ocr/extract`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPdfResults([...pdfResults, response.data]);
      toast.success('Dados extraídos do PDF com sucesso!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('OCR de PDF é exclusivo do Plano PRO!');
      } else {
        toast.error('Erro ao processar PDF');
      }
    } finally {
      setUploadingPDF(false);
    }
  };

  const handlePDFUpload = (e) => {
    const file = e.target.files[0];
    processPDFFile(file);
  };

  // ── Drag and Drop Handlers para CSV ──────────────────────────────────────────
  const handleDragOverCSV = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCSV(true);
  }, []);

  const handleDragLeaveCSV = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCSV(false);
  }, []);

  const handleDropCSV = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingCSV(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processCSVFile(files[0]);
    }
  }, []);

  // ── Drag and Drop Handlers para PDF ──────────────────────────────────────────
  const handleDragOverPDF = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPDF(true);
  }, []);

  const handleDragLeavePDF = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPDF(false);
  }, []);

  const handleDropPDF = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPDF(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processPDFFile(files[0]);
    }
  }, [pdfResults]);

  // ── Criar lembrete a partir de PDF extraído ──────────────────────────────────
  const createFromPDF = async (data) => {
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.post(`${API}/lembretes`, {
        cliente_nome: data.cliente_nome,
        cliente_email: data.cliente_email || '',
        cliente_telefone: data.cliente_telefone || '',
        tipo_servico: data.tipo_servico,
        data_vigencia: data.data_vigencia,
        data_renovacao: data.data_renovacao,
        recorrencia: data.recorrencia || 'anual',
        observacoes: 'Importado via PDF'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Lembrete criado!');
      setPdfResults(pdfResults.filter(r => r !== data));
    } catch (error) {
      toast.error('Erro ao criar lembrete');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Importações</h1>
        <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Importe lembretes em massa via CSV ou extraia dados de PDFs</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* CSV Import */}
          <Card className="p-4 sm:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <Table className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Importação CSV</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Importe múltiplos lembretes de uma só vez
                </p>
                <span className="inline-block mt-2 px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                  Exclusivo PRO
                </span>
              </div>
            </div>

            {/* Drop Zone CSV */}
            <div
              onDragOver={handleDragOverCSV}
              onDragLeave={handleDragLeaveCSV}
              onDrop={handleDropCSV}
              onClick={() => csvInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-2xl p-4 sm:p-8 text-center mb-4 cursor-pointer
                transition-all duration-200
                ${isDraggingCSV 
                  ? 'border-green-500 bg-green-100 scale-[1.02]' 
                  : 'border-green-200 bg-green-50/50 hover:border-green-400 hover:bg-green-100/50'
                }
                ${uploadingCSV ? 'pointer-events-none opacity-60' : ''}
              `}
            >
              {uploadingCSV ? (
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-green-600 mx-auto mb-3 sm:mb-4 animate-spin" />
              ) : (
                <Upload className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 transition-transform ${isDraggingCSV ? 'text-green-600 scale-110' : 'text-green-600'}`} />
              )}
              
              <p className="text-xs sm:text-sm text-gray-600 mb-2">
                {isDraggingCSV ? (
                  <span className="font-semibold text-green-700">Solte o arquivo aqui!</span>
                ) : (
                  <>
                    <span className="hidden sm:inline">Arraste seu arquivo CSV aqui ou clique para selecionar</span>
                    <span className="sm:hidden">Toque para selecionar seu CSV</span>
                  </>
                )}
              </p>
              
              <input
                ref={csvInputRef}
                data-testid="csv-upload-input"
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                disabled={uploadingCSV}
                className="hidden"
              />
              
              <Button
                type="button"
                data-testid="csv-upload-button"
                disabled={uploadingCSV}
                className="rounded-2xl bg-green-600 hover:bg-green-700 text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  csvInputRef.current?.click();
                }}
              >
                {uploadingCSV ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Table className="w-4 h-4 mr-2" />
                    Selecionar CSV
                  </>
                )}
              </Button>
            </div>

            {csvResults && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-medium text-green-900 text-sm sm:text-base">Importação concluída!</span>
                </div>
                <p className="text-xs sm:text-sm text-green-700">
                  {csvResults.imported} lembretes importados de {csvResults.total} linhas
                </p>
                {csvResults.errors > 0 && (
                  <p className="text-xs sm:text-sm text-orange-600 mt-1">
                    {csvResults.errors} linha(s) com erro foram ignoradas
                  </p>
                )}
              </div>
            )}

            <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-600 font-medium mb-2">Exemplo de CSV:</p>
              <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                <pre className="text-[10px] sm:text-xs text-gray-700 bg-white p-2 rounded border whitespace-pre-wrap break-all sm:whitespace-pre sm:break-normal">
{`cliente_nome,cliente_email,cliente_telefone,tipo_servico,data_vigencia,data_renovacao
João Silva,joao@email.com,11999998888,Seguro Auto,2026-01-01,2027-01-01
Maria Santos,maria@email.com,21988887777,Plano Saúde,2026-02-01,2027-02-01`}
                </pre>
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-2">
                💡 Campos opcionais: cliente_email, cliente_telefone, recorrencia
              </p>
            </div>
          </Card>

          {/* PDF OCR */}
          <Card className="p-4 sm:p-6 rounded-2xl border shadow-sm">
            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Extração de PDF (OCR)</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Extraia dados de apólices e propostas automaticamente
                </p>
                <span className="inline-block mt-2 px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                  Exclusivo PRO
                </span>
              </div>
            </div>

            {/* Drop Zone PDF */}
            <div
              onDragOver={handleDragOverPDF}
              onDragLeave={handleDragLeavePDF}
              onDrop={handleDropPDF}
              onClick={() => pdfInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-2xl p-4 sm:p-8 text-center mb-4 cursor-pointer
                transition-all duration-200
                ${isDraggingPDF 
                  ? 'border-red-500 bg-red-100 scale-[1.02]' 
                  : 'border-red-200 bg-red-50/50 hover:border-red-400 hover:bg-red-100/50'
                }
                ${uploadingPDF ? 'pointer-events-none opacity-60' : ''}
              `}
            >
              {uploadingPDF ? (
                <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4 animate-spin" />
              ) : (
                <FileText className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 transition-transform ${isDraggingPDF ? 'text-red-600 scale-110' : 'text-red-500'}`} />
              )}
              
              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                {isDraggingPDF ? (
                  <span className="font-semibold text-red-700">Solte o arquivo aqui!</span>
                ) : (
                  <>
                    <span className="hidden sm:inline">Arraste apólices, propostas ou documentos PDF aqui</span>
                    <span className="sm:hidden">Toque para selecionar seu PDF</span>
                  </>
                )}
              </p>
              
              <input
                ref={pdfInputRef}
                data-testid="pdf-upload-input"
                type="file"
                accept=".pdf"
                onChange={handlePDFUpload}
                disabled={uploadingPDF}
                className="hidden"
              />
              
              <Button
                type="button"
                data-testid="pdf-upload-button"
                disabled={uploadingPDF}
                className="rounded-2xl bg-primary hover:bg-primary-hover text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  pdfInputRef.current?.click();
                }}
              >
                {uploadingPDF ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Extraindo...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Selecionar PDF
                  </>
                )}
              </Button>
            </div>

            {pdfResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs sm:text-sm font-medium text-gray-700">Dados extraídos:</p>
                {pdfResults.map((result, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4">
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mb-3">
                      <div className="min-w-0">
                        <span className="text-gray-500">Cliente:</span>
                        <p className="font-medium text-gray-900 truncate">{result.cliente_nome}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-500">Serviço:</span>
                        <p className="font-medium text-gray-900 truncate">{result.tipo_servico}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Vigência:</span>
                        <p className="font-medium text-gray-900">{result.data_vigencia}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Renovação:</span>
                        <p className="font-medium text-gray-900">{result.data_renovacao}</p>
                      </div>
                    </div>
                    <Button
                      data-testid={`create-from-pdf-${index}`}
                      onClick={() => createFromPDF(result)}
                      size="sm"
                      className="w-full rounded-xl bg-primary hover:bg-primary-hover text-sm"
                    >
                      Criar Lembrete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Importacoes;
